import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { inputClass } from '@/components/ui/input';

export type CustomDropdownOption = {
  value: string;
  label: string;
};

type CustomDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  options: CustomDropdownOption[];
  placeholder: string;
  ariaLabel?: string;
  name?: string;
  className?: string;
};

export function CustomDropdown({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  name,
  className = '',
}: CustomDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutsideClick(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`flex h-11 items-center justify-between text-left cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] ${inputClass}`}
      >
        <span className={selected ? 'text-[#0a0a0a]' : 'text-[#a8a29e]'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className="h-4 w-4 text-[#a8a29e] transition-transform duration-150"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-[#e7e5e4] bg-white shadow-lg"
        >
          {options.map((option) => {
            const active = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors hover:bg-[#f5f5f4] focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
                style={{ color: active ? '#F26B1D' : '#57534e' }}
              >
                <span>{option.label}</span>
                {active && <span className="text-xs font-medium text-[#F26B1D]">Selected</span>}
              </button>
            );
          })}
        </div>
      )}

      {name ? <input type="hidden" name={name} value={value} /> : null}
    </div>
  );
}
